import clsx from 'clsx';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Avatar,
  AvatarImage,
  Button,
  buttonVariants,
  Label,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@playcastdotio/Shadcn';
import {
  faBars,
  faChevronDown,
  faChevronUp,
  faCircleUser as faCircleUserSolid,
  faComputer,
  faGamepadAlt,
  faGear,
  faUser,
  faWindowMaximize,
  faWindowMinimize,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { faCircleExclamation } from '@fortawesome/pro-light-svg-icons';
import {
  faArrowRightFromBracket,
  faCameraMovie,
  faCircleQuestion,
  faCircleUser as faCircleUserLight,
  faClapperboardPlay,
} from '@fortawesome/pro-light-svg-icons';
import { isElectron } from '../api';
import { useCallback, useContext, useEffect, useRef, useState, useMemo } from 'react';
import { PlaycastContext } from './PlaycastWrapper';
import pcLogo from '../../assets/playcast_logo-96x96.png';
import { Station } from './layout/Station';
import { SettingsModal } from './widgets/SettingsModal';
import { SettingsWarningModal } from './widgets/SettingsWarningModal';
import { useAuth, useClerk, useUser, useSession } from '@clerk/clerk-react';
import { ClerkProfileSettings } from './ClerkProfileSettings';
import { RecordKey } from './widgets/RecordKey';
import { ThemePicker } from './widgets/ThemePicker';
import { IdentityPlatform } from '@playcastdotio/CoreTypes';
import { determineProfileSlug } from '@playcastdotio/SharedComponents';
import constants from '@playcastdotio/GlobalConfig';
import { faWindowRestore } from '@fortawesome/pro-solid-svg-icons';
import * as Sentry from '@sentry/react';
import { OWNER_AUTH_ID_STORAGE_KEY } from '../api/stores/shared';
import { serviceContainer } from '../services/ServiceContainer';

export const DefaultNavbar = () => {
  const context = useContext(PlaycastContext);
  const location = useLocation();
  const nav = [
    {
      icon: faCameraMovie,
      text: 'Studio',
      path: '/studio',
      testId: 'studio-nav-item',
    },
    // {
    //   icon: faClapperboardPlay,
    //   text: 'Playcast',
    //   path: '/playcast',
    //   testId: 'playcast-nav-item',
    // },
    // {
    //   icon: faList,
    //   text: 'Lobby',
    //   path: '/lobby',
    //   testId: 'lobby-nav-item',
    // },
    {
      icon: faClapperboardPlay,
      text: 'Playcast',
      path: '/playcast',
      testId: 'playcast-nav-item',
    },
    {
      icon: faGamepadAlt,
      text: 'Inputs',
      path: '/inputs',
      testId: 'inputs-nav-item',
      extra: true,
    },
  ];
  const [showNav, setShowNav] = useState(false);
  const [showExtraNav, setShowExtraNav] = useState(false);

  // if Ctrl+F11 is hit, show/hide
  window.addEventListener('keydown', (e) => {
    if (e.key === 'F11' && e.ctrlKey) {
      setShowExtraNav(!showExtraNav);
    }
  });

  useEffect(() => {
    if (context.stateStores.functionConnectionState.connected) {
      setShowNav(true);
    } else {
      setShowNav(false);
    }
  }, [context.stateStores.functionConnectionState.connected]);

  return (
    <div className='text-primary border-primary bg-darker strong-shadows flex w-full justify-center border-b-2'>
      <div className='flex justify-between gap-4 p-2 md:gap-8'>
        {showNav ? (
          nav.map((item) =>
            !item.extra || showExtraNav ? (
              <Link
                key={item.text}
                to={item.path}
                data-testid={item.testId}
                className={clsx(
                  item.path === location.pathname ? 'text-amber-400 hover:bg-inherit hover:opacity-80' : 'text-gray-100 hover:opacity-80',
                  'flex items-center font-[Syne] text-lg font-semibold md:text-3xl',
                )}
              >
                <FontAwesomeIcon
                  icon={item.icon}
                  className={clsx(
                    item.path === location.pathname
                      ? 'group-hover:text-midnight-darker text-amber-400'
                      : 'text-gray-100 group-hover:text-gray-300',
                    'mr-2 h-5',
                  )}
                />
                <span>{item.text}</span>
              </Link>
            ) : null,
          )
        ) : (
          <div className='group h-fit w-fit'>
            <span
              style={{ visibility: 'hidden' }}
              className={clsx(
                'text-gray-100 group-hover:text-gray-300',
                'flex items-center px-4 font-[Syne] text-xl font-semibold md:text-3xl',
              )}
            >
              |
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export const MobileNavbar = () => {
  const context = useContext(PlaycastContext);
  const [hosts, setHosts] = useState<HostItems>([]);
  const [selectedHostId, setSelectedHostId] = useState(context.stateStores.remoteHostList.selectedHostId);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const { isSignedIn } = useAuth();
  const { openSignIn, signOut } = useClerk();
  const [showProfileNav, setShowProfileNav] = useState(false);
  const [showSettingsWarning, setShowSettingsWarning] = useState(false);

  // Use the extracted component
  const { openUserProfileSettings } = ClerkProfileSettings();

  const handleQrCodeSwitch = async (showStreamQrCode: boolean) => {
    context.targetFunctions.updateShowStreamQrCode(!showStreamQrCode);
    if (showStreamQrCode) {
      context.targetFunctions.overlayDeleteOverlay('qr-code');
    } else {
      try {
        await context.targetFunctions.createQrCodeOverlay();
      } catch (error) {
        console.error('Failed to create QR code overlay:', error);
        // Revert the switch if overlay creation fails
        context.targetFunctions.updateShowStreamQrCode(showStreamQrCode);
      }
    }
  };

  // Memoized function to get hosts
  const getHosts = useCallback(() => {
    const { hosts, inactive } = context.stateStores.remoteHostList;

    const newHosts: HostItems = [];

    // Active hosts
    for (const uniqueId in hosts) {
      const host = hosts[uniqueId];
      const active = inactive[uniqueId] ? false : true;
      newHosts.push({
        displayName: host.displayName,
        remoteId: host.remoteId,
        active: active,
        playerCount: host.joinedCount,
      });
    }

    // Inactive hosts
    for (const uniqueId in inactive) {
      const host = inactive[uniqueId];
      newHosts.push({
        displayName: host.displayName,
        remoteId: host.remoteId,
        active: false,
        playerCount: 0,
      });
    }

    setHosts(newHosts);
    // console.log('Hosts:', newHosts);
  }, [context.stateStores.remoteHostList.hosts, context.stateStores.remoteHostList.inactive]);

  // Sync hosts on dependency changes
  useEffect(() => {
    getHosts();
  }, [getHosts]);

  // Sync selected host ID on dependency changes
  useEffect(() => {
    setSelectedHostId(context.stateStores.remoteHostList.selectedHostId);
  }, [context.stateStores.remoteHostList.selectedHostId]);

  // Handle clicked event
  const handleClicked = useCallback(
    (remoteId: string) => {
      const mobileMenu = document.getElementById('mobile-menu') as HTMLInputElement;
      if (mobileMenu) {
        mobileMenu.checked = false;
      }
      context.changeFunctionTarget(remoteId);
    },
    [context],
  );

  const handleLoginLogout = async () => {
    if (isSignedIn) {
      try {
        await serviceContainer.analyticsManager.sendUserLoggedOutEvent(
          isElectron ? 'electron' : 'remote',
        );
        localStorage.removeItem(OWNER_AUTH_ID_STORAGE_KEY);
        await signOut();
        window.location.reload();
      } catch (error) {
        console.error('Error signing out:', error);
      }
    } else {
      openSignIn();
    }
  };

  const handleSettingsClicked = () => {
    if (context.stateStores.hostState.status === 'streaming') {
      // setShowSettingsWarning(true);
      openUserProfileSettings();
      // setShowSettingsModal(false);
    } else {
      // setShowSettingsModal(true);
      openUserProfileSettings();
    }
  };

  return (
    <header className='text-content-text border-midnight-dark relative z-40 flex w-full items-center border-b-2 px-4 align-middle lg:hidden'>
      <nav id='mobile menu' className='flex items-center justify-start gap-x-2 p-2 align-middle'>
        <label className='rtl relative z-40 cursor-pointer px-3 py-6' htmlFor='mobile-menu' data-testid='mobile-menu-toggle'>
          <input className='peer hidden' type='checkbox' id='mobile-menu' />
          <div className="relative z-50 block h-[1px] w-7 bg-transparent bg-white content-[''] before:absolute before:top-[-0.35rem] before:z-50 before:block before:h-full before:w-full before:bg-white before:transition-all before:duration-200 before:ease-out before:content-[''] after:absolute after:bottom-[-0.35rem] after:left-0 after:block after:h-full after:w-full after:bg-white after:transition-all after:duration-200 after:ease-out after:content-[''] peer-checked:bg-transparent before:peer-checked:top-0 before:peer-checked:w-full before:peer-checked:rotate-45 before:peer-checked:transform after:peer-checked:bottom-0 after:peer-checked:w-full after:peer-checked:-rotate-45 after:peer-checked:transform"></div>
          <div className='fixed inset-0 z-40 hidden h-full w-full bg-black/50 backdrop-blur-sm peer-checked:block'>&nbsp;</div>
          <div className='fixed left-0 top-0 z-40 h-full w-full -translate-x-full overflow-y-auto overscroll-y-none transition duration-500 peer-checked:translate-x-0'>
            <div className='bg-midnight float-left min-h-full w-[85%] px-6 pt-12 shadow-2xl'>
              <menu>
                <ul className='ltr flex h-min flex-col items-center gap-y-8 py-12 align-middle text-2xl'>
                  {hosts.map((host, index) => (
                    <li key={index}>
                      <Link
                        to='/'
                        className={clsx(
                          'hover:text-midnight-brighter focus:text-midnight-brighter active:text-midnight-brighter flex flex-row items-center gap-x-4',
                          selectedHostId === host.remoteId ? 'text-amber-400' : 'text-white',
                        )}
                        onClick={() => handleClicked(host.remoteId)}
                        data-testid={`host-link-${host.remoteId}`}
                      >
                        {host.displayName}
                      </Link>
                    </li>
                  ))}
                </ul>
              </menu>
            </div>
          </div>
        </label>
        <span>Stations</span>
      </nav>

      <div id='mobile logo' className='flex flex-grow flex-row justify-center gap-x-2 align-middle'>
        <img src={pcLogo} alt='Logo' className='h-10 w-10' />
      </div>

      <div>
        <button
          className='flex cursor-pointer items-center gap-2'
          onClick={() => setShowProfileNav(!showProfileNav)}
          data-testid='profile-nav-toggle'
        >
          <Label className='text-md'>Profile</Label>
          <FontAwesomeIcon icon={faCircleUserLight} className='size-6' />
        </button>
        {showProfileNav && (
          <div
            className={clsx(
              isSignedIn ? '-bottom-24' : '-bottom-[3.8rem]',
              'bg-midnight absolute right-2 flex flex-col gap-2 rounded-b-lg p-4',
            )}
          >
            {isSignedIn && (
              <button className='flex cursor-pointer items-center gap-2' onClick={handleSettingsClicked} data-testid='settings-button'>
                <FontAwesomeIcon icon={faGear} />
                <Label className='text-midnight-white cursor-pointer font-[Syne] text-lg'>Settings</Label>
              </button>
            )}
            <button className='flex cursor-pointer items-center gap-2' onClick={handleLoginLogout} data-testid='login-logout-button'>
              <FontAwesomeIcon icon={faArrowRightFromBracket} />
              <Label className='text-midnight-white cursor-pointer font-[Syne] text-lg'>{isSignedIn ? 'Logout' : 'Login'}</Label>
            </button>
          </div>
        )}
      </div>

      <SettingsWarningModal state={[showSettingsWarning, setShowSettingsWarning]} />
      <SettingsModal state={[showSettingsModal, setShowSettingsModal]} />
    </header>
  );
};

type HostItems = { displayName: string; remoteId: string; active: boolean; playerCount: number }[];

export const StationNavBar = () => {
  const context = useContext(PlaycastContext);
  const [showStation, setShowStation] = useState(true);

  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const [hosts, setHosts] = useState<HostItems>([]);

  const getHosts = useCallback(() => {
    const newHosts: HostItems = [];
    Object.keys(context.stateStores.remoteHostList.hosts).forEach((uniqueId) => {
      const host = context.stateStores.remoteHostList.hosts[uniqueId];
      newHosts.push({
        displayName: host.displayName,
        remoteId: host.remoteId,
        active: !Object.keys(context.stateStores.remoteHostList.inactive).includes(uniqueId) ? true : false,
        playerCount: host.joinedCount,
      });
    });
    Object.keys(context.stateStores.remoteHostList.inactive).forEach((uniqueId) => {
      const host = context.stateStores.remoteHostList.inactive[uniqueId];
      newHosts.push({
        displayName: host.displayName,
        remoteId: host.remoteId,
        active: false,
        playerCount: 0,
      });
    });
    setHosts(newHosts);
  }, [context.stateStores.remoteHostList.hosts, context.stateStores.remoteHostList.inactive]);

  useEffect(() => {
    const handleScroll = () => {
      if (scrollRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;

        setShowScrollToTop(scrollTop > 0);
        setShowScrollToBottom(scrollTop + clientHeight < scrollHeight);
      }
    };

    const scrollableDiv = scrollRef.current as HTMLElement | null;
    if (scrollableDiv) {
      handleScroll();
      scrollableDiv.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (scrollableDiv) {
        scrollableDiv.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  useEffect(() => {
    getHosts();
  }, [context.stateStores.remoteHostList.hosts, context.stateStores.remoteHostList.inactive, getHosts]);

  // useEffect(() => {
  //   getHosts();
  // }, []);

  const scrollToTop = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  if (isElectron) return;

  return (
    <div
      className={clsx(
        showStation ? '' : 'absolute -translate-x-52',
        'z-[10] hidden h-full w-60 flex-none transition-all duration-300 lg:flex',
      )}
    >
      <div className='text-primary relative flex w-full flex-col'>
        <div className='absolute top-0 flex w-60 justify-end space-x-2 rounded-br-lg bg-gray-700 px-1 py-1.5'>
          <Button
            variant='ghost'
            size='icon'
            className='m-0 h-6 w-6 p-0 text-xl text-gray-900 hover:bg-inherit hover:text-gray-800'
            onClick={() => setShowStation(!showStation)}
            data-testid='toggle-station-button'
          >
            <FontAwesomeIcon icon={faBars} />
          </Button>
        </div>
        <div ref={scrollRef} className='rtl relative flex h-full w-52 flex-col items-center overflow-y-auto bg-gray-700'>
          {showScrollToTop && (
            <button
              onClick={scrollToTop}
              className='top-18 text-midnight-darkest fixed left-0 z-10 w-52 bg-gray-800/90 p-1 focus:outline-none'
              data-testid='scroll-to-top-button'
            >
              <FontAwesomeIcon icon={faChevronUp} className='text-2xl' />
            </button>
          )}
          {showScrollToBottom && (
            <button
              onClick={scrollToBottom}
              className='text-midnight-darkest fixed bottom-0 left-0 z-10 w-52 bg-gray-800/90 p-1 focus:outline-none'
              data-testid='scroll-to-bottom-button'
            >
              <FontAwesomeIcon icon={faChevronDown} className='text-2xl' />
            </button>
          )}
          <div className='ltr flex w-full flex-1 flex-col space-y-2 p-2'>
            {hosts.map((host, index) => (
              <Station
                key={index}
                displayName={host.displayName}
                remoteId={host.remoteId}
                active={host.active}
                playerCount={host.playerCount}
                data-testid={`station-${host.remoteId}`}
              />
            ))}
            {/* {...[...Array(10)].map((_, index) => (
              <Station key={index} displayName='STS.Slap City 1' remoteId='asd123' active={true} playerCount={index} />
            ))} */}
          </div>
        </div>
      </div>
    </div>
  );
};

export const MenuStateBar = () => {
  const context = useContext(PlaycastContext);
  const { isSignedIn, isLoaded } = useAuth();
  const { session } = useSession();
  const { user } = useUser();
  const { signOut } = useClerk();
  const electronFunctions = context.electronFunctions;
  const [accessToken, setAccessToken] = useState('');
  const [email, setEmail] = useState('');
  const [picture, setPicture] = useState('');
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showSettingsWarning, setShowSettingsWarning] = useState(false);
  const [showAppReminderTooltip, setShowAppReminderTooltip] = useState(false);
  const stateStores = context.stateStores;
  const [edition, setEdition] = useState(context.stateStores.hostState.edition);

  // Generate profile URL for Pavilion
  const getProfileUrl = useCallback(() => {
    if (!isLoaded || !isSignedIn || !user) {
      return 'https://pavilion.playcast.io';
    }

    // Get the appropriate profile slug
    const profileSlug = determineProfileSlug({
      authId: user.id,
      username: user.username ?? '',
      platform: IdentityPlatform.CLERK,
    });

    const baseUrl = constants.PAVILION_PLAYER_BASE_URL;
    // Ensure baseUrl ends with a trailing slash
    const baseUrlWithTrailingSlash = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;

    return profileSlug ? `${baseUrlWithTrailingSlash}${profileSlug}` : baseUrlWithTrailingSlash;
  }, [isLoaded, isSignedIn, user]);

  // Calculate profile URL whenever user changes
  const profileUrl = useMemo(() => getProfileUrl(), [getProfileUrl]);

  useEffect(() => {
    setEdition(context.stateStores.hostState.edition);
  }, [context.stateStores.hostState.edition]);

  // Use the extracted component
  const { openUserProfileSettings } = ClerkProfileSettings();

  const handleLogin = () => {
    if (isElectron) {
      electronFunctions?.hostLogin();
    } else {
      console.error('ERROR: User should already be logged in, if required to login, it should be handled by the clerk button');
    }
  };

  const handleLogout = async () => {
    // Awaited before signing out so the request is dispatched while the identity
    // store still holds the IDs, and before the reload below.
    await serviceContainer.analyticsManager.sendUserLoggedOutEvent(
      isElectron ? 'electron' : 'remote',
    );
    localStorage.removeItem(OWNER_AUTH_ID_STORAGE_KEY);
    if (isElectron) {
      context.targetFunctions.stopStream();
      try {
        // await signOut();
        await session?.end();
      } catch (e) {
        console.error('Error logging out:', e);
        electronFunctions?.hostLogout();
        window.location.reload();
      }
      // electronFunctions?.hostLogout();
      window.location.reload();
    } else {
      console.log('Signing out');
      try {
        await signOut();
        window.location.reload();
      } catch (e) {
        console.error('Error logging out:', e);
      }
    }
  };

  useEffect(() => {
    setAccessToken(stateStores.loginProfileState.accessToken);
    setEmail(stateStores.loginProfileState.email);
    setPicture(stateStores.loginProfileState.picture);
  }, [stateStores.loginProfileState]);

  const handleSettingsClicked = () => {
    if (context.stateStores.hostState.status === 'streaming') {
      // setShowSettingsWarning(true);
      openUserProfileSettings();
      // setShowSettingsModal(false);
    } else {
      // setShowSettingsModal(true);
      openUserProfileSettings();
    }
  };

  const generateFaqUrl = (): string => {
    const branchName = constants.VERSION.split('-')[1];

    switch (branchName) {
      case 'local':
        return 'http://localhost:4204/#faq-section';
      case 'prerelease':
        return 'https://prerelease.marketing.playcast.io/#faq-section';
      case 'dev':
        return 'https://dev.marketing.playcast.io/#faq-section';
      default:
        return 'https://playcast.io/#faq-section';
    }
  };

  const handleFaqClicked = () => {
    const url = generateFaqUrl();
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleRemoteControlClicked = () => {
    const url = constants.DASHBOARD_BASE_URL;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleMinimize = () => {
    if (electronFunctions) {
      // electronFunctions.setWindowState('minimized');
      context.targetFunctions.setWindowState('minimized');
    }
  };

  const handleMaximizeRestore = async () => {
    if (!electronFunctions) return;
    if (stateStores.windowState === 'maximized') {
      // electronFunctions.setWindowState('maximized');
      context.targetFunctions.setWindowState('normal');
    } else {
      // electronFunctions.setWindowState('maximized');
      context.targetFunctions.setWindowState('maximized');
    }
  };

  const handleClose = () => {
    if (electronFunctions) {
      if(window.playcast?.guestAccount) {
        Sentry.captureMessage('Guest Mode Host App Closed', {
          level: 'error',
          tags: {
            event_type: 'guest_mode_host_app_closed_from_close_button',
            guest_mode: String(window.playcast?.guestAccount),
            version: constants.VERSION,
          },
        });
      }
      electronFunctions.setWindowState('closed');
    }
  };

  const handleAppReminderClick = () => {
    setShowAppReminderTooltip(!showAppReminderTooltip);
  };

  return (
    <div className='bg-primary title-bar z-50 hidden h-10 w-full items-center gap-x-2 lg:flex'>
      <div className='ml-2 flex shrink-0 items-center gap-x-2'>
        <img src={pcLogo} alt='Logo' className='h-7 w-7' />
        <h1 className='text-md font-headline text-midnight-white font-semibold'>Playcast Host</h1>
      </div>
      <div className='flex-1' />
      {edition === 'standard' && <RecordKey context={context} />}
      <div className={clsx(!electronFunctions ? 'mr-2' : '', 'title-bar-buttons flex gap-x-2')}>
        {accessToken ? (
          <button
            data-testid='logout-button'
            className='theme-midnight:shadow-[inset_2px_3px_2px_#A9E8EA,inset_-2px_-3px_2px_#2D618C] bg-bright hover:bg-brighter group flex items-center gap-x-2 rounded-lg px-2'
            onClick={handleLogout}
          >
            <div className=''>
              <Avatar className='h-6 w-6'>
                <AvatarImage src={picture} alt='profile-image' />
              </Avatar>
            </div>
            <Label className='text-md text-midnight-darkest group-hover:text-midnight-darker cursor-pointer'>Logout</Label>
          </button>
        ) : (
          <Button
            data-testid='login-button'
            className='theme-midnight:shadow-[inset_2px_3px_2px_#A9E8EA,inset_-2px_-3px_2px_#2D618C] text-darkest bg-bright focus:bg-bright active:bg-bright hover:bg-brighter m-0 h-8 w-fit cursor-pointer px-3 font-semibold'
            onClick={handleLogin}
          >
            <FontAwesomeIcon icon={faCircleUserSolid} className='text-midnight-darkest mr-2 h-6' /> Login
          </Button>
        )}

        {/* Theme Picker Button */}
        <ThemePicker data-testid='theme-picker' />
        {edition === 'standard' ? (
          <>
            <Button
              className='text-primary bg-bright focus:bg-bright active:bg-bright hover:bg-brighter theme-midnight:shadow-[inset_2px_3px_2px_#A9E8EA,inset_-2px_-3px_2px_#2D618C] m-0 size-8 cursor-pointer p-0 px-3 font-semibold'
              onClick={accessToken ? handleSettingsClicked : handleLogin}
              data-testid='settings-button-desktop'
            >
              <FontAwesomeIcon icon={faGear} className='text-midnight-darkest h-6 w-6' />
            </Button>

            <TooltipProvider>
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild id='remote-control-tooltip-trigger'>
                  <Button
                    className='text-primary bg-bright focus:bg-bright active:bg-bright hover:bg-brighter theme-midnight:shadow-[inset_2px_3px_2px_#A9E8EA,inset_-2px_-3px_2px_#2D618C] m-0 size-8 cursor-pointer p-0 px-3 font-semibold'
                    onClick={handleRemoteControlClicked}
                    data-testid='remote-control-button'
                  >
                    <FontAwesomeIcon icon={faComputer} className='text-midnight-darkest h-6 w-6' />
                  </Button>
                </TooltipTrigger>
                <TooltipContent id='remote-control-tooltip-content'>
                  <p>Manage your session remotely on a PC or phone through our website.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </>
        ) : (
          <Link
            to={profileUrl}
            className={clsx(
              buttonVariants(),
              '!text-primary !bg-bright focus:!bg-bright active:!bg-bright hover:!bg-brighter theme-midnight:!shadow-[inset_2px_3px_2px_#A9E8EA,inset_-2px_-3px_2px_#2D618C] !m-0 !size-8 !cursor-pointer !p-0 !px-3 !font-semibold',
            )}
            data-testid='pavilion-profile-button'
          >
            <FontAwesomeIcon icon={faUser} className='text-midnight-darkest size-5' />
          </Link>
        )}
        <TooltipProvider>
          <Tooltip delayDuration={300}>
            <TooltipTrigger asChild id='faq-tooltip-trigger'>
              <Button
                className='text-primary bg-bright focus:bg-bright active:bg-bright hover:bg-brighter theme-midnight:shadow-[inset_2px_3px_2px_#A9E8EA,inset_-2px_-3px_2px_#2D618C] m-0 size-8 cursor-pointer p-0 px-3 font-semibold'
                onClick={handleFaqClicked}
                data-testid='faq-button'
              >
                <FontAwesomeIcon icon={faCircleQuestion} className='text-midnight-darkest h-6 w-6' />
              </Button>
            </TooltipTrigger>
            <TooltipContent id='faq-tooltip-content'>
              <p>Opens the FAQ page on our website.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      {electronFunctions && (
        <section className='title-bar-buttons z-30 flex'>
          <Button
            onClick={handleMinimize}
            size='icon'
            variant='ghost'
            className='hover:text-midnight-bright text-midnight-white h-10 w-9 rounded-none hover:bg-inherit'
            data-testid='minimize-window-button'
          >
            <FontAwesomeIcon icon={faWindowMinimize} />
          </Button>
          <Button
            onClick={handleMaximizeRestore}
            size='icon'
            variant='ghost'
            className='hover:text-midnight-bright text-midnight-white h-10 w-9 rounded-none hover:bg-inherit'
            data-testid='maximize-window-button'
          >
            <FontAwesomeIcon icon={stateStores.windowState === 'maximized' ? faWindowRestore : faWindowMaximize} />
          </Button>
          <Button
            onClick={handleClose}
            size='icon'
            variant='ghost'
            className='hover:text-midnight-white text-midnight-white h-10 w-9 rounded-none hover:bg-inherit hover:bg-red-500'
            data-testid='close-window-button'
          >
            <FontAwesomeIcon icon={faXmark} />
          </Button>
        </section>
      )}
      <SettingsWarningModal state={[showSettingsWarning, setShowSettingsWarning]} />
      <SettingsModal state={[showSettingsModal, setShowSettingsModal]} />
    </div>
  );
};
